import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { MapViewDirections, MapViewTypes } from "../../../interfaces/MapView.interface";
import GlobalAlert2 from "../../home/GlobalAlert2";
import { updateMapViewPinArray, updateOpenSelectMode } from "../../../redux/mapView/mapViewSlice";
import MapViewPin from "./MapViewPin";
// REDUX
import GETJwtToken from "../../../redux/authentication/authentication";
import { usePostMapViewPinMutation } from "../../../redux/mapView/mapViewAPI";
import useMousePosition from "./useMousePosition";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";

export function resolvePublicAssetUrl(path: string): string {
  if (!path) {
    return path;
  }

  if (/^(https?:)?\/\//i.test(path) || path.startsWith("data:")) {
    return path;
  }

  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${normalizedPath}`;
}

function MapViewMap() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [svgSrc, setSvgSrc] = React.useState<string>("");
  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [buttonProperties, setButtonProperties] = React.useState({
    width: 0,
    height: 0,
  });
  const { x, y } = useMousePosition();
  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { openSelectMode, selectedPin, mapViewPinArray, mapViewItem } = useSelector(
    (state: any) => state.mapView,
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerPostMapViewPin,
    { isSuccess: isSuccessPostMapView, isError: isErrorPostMapView, error: errorPostMapView },
  ] = usePostMapViewPinMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getDirection = (direction: string) => {
    if (direction === MapViewDirections.UP) return "top";
    if (direction === MapViewDirections.DOWN) return "bottom";
    if (direction === MapViewDirections.LEFT) return "left";
    if (direction === MapViewDirections.RIGHT) return "right";
    return "top";
  };

  const getTypeInLowerCase = (type: string) => {
    if (type === MapViewTypes.LIFT) return "elevator";
    if (type === MapViewTypes.ESCALATOR) return "escalator";
    if (type === MapViewTypes.MOVINGWALK) return "movingwalk";
    if (type === MapViewTypes.MAP) return "map";
    return "elevator";
  };
  const handleResize = () => {
    const width = containerRef.current?.offsetWidth || 0;
    const height = containerRef.current?.offsetHeight || 0;
    if (width === 0 || height === 0) return;
    setButtonProperties({
      width,
      height,
    });
  };

  const getLiftIcon = (type: string) => {
    if (type === MapViewTypes.LIFT) return "elClose";
    if (type === MapViewTypes.ESCALATOR) return "esLocked";
    if (type === MapViewTypes.MOVINGWALK) return "weLocked";
    if (type === MapViewTypes.MAP) return "map";
    return "elClose";
  };

  const getXMargin = (direction: MapViewDirections) => {
    switch (direction) {
      case MapViewDirections.LEFT:
        return -8;
      case MapViewDirections.RIGHT:
        return 96;
      case MapViewDirections.UP:
        return 20;
      case MapViewDirections.DOWN:
        return 20;
      default:
        return 0;
    }
  };

  const getYMargin = (direction: MapViewDirections) => {
    switch (direction) {
      case MapViewDirections.DOWN:
        return 80;
      case MapViewDirections.LEFT:
        return 18;
      case MapViewDirections.RIGHT:
        return 18;
      case MapViewDirections.UP:
        return -8;
      default:
        return 0;
    }
  };

  const handleClickMap = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!openSelectMode) return;
    const offsetX = e.nativeEvent.offsetX - getXMargin(selectedPin.direction);
    const offsetY = e.nativeEvent.offsetY - getYMargin(selectedPin.direction);

    const relativeX = offsetX / buttonProperties.width;
    const relativeY = offsetY / buttonProperties.height;

    // save in redux
    dispatch(
      updateMapViewPinArray([
        ...mapViewPinArray,
        {
          ...selectedPin,
          x: relativeX,
          y: relativeY,
        },
      ]),
    );
    // Save map view in the backend
    const token = await GETJwtToken();
    const body = {
      id: mapViewItem.id,
      name: mapViewItem.name,
      org_id: mapViewItem.orgId,
      map_img_url: mapViewItem.map_img_url || mapViewItem.mapImgUrl || mapViewItem.s3_url || "",
      has_display_view: Boolean(mapViewItem.has_display_view),
      is_main: Boolean(mapViewItem.is_main ?? mapViewItem.isMain ?? false),
      offline_url: mapViewItem.offline_url || "",
      waypoints: [
        ...mapViewPinArray,
        {
          ...selectedPin,
          x: relativeX,
          y: relativeY,
          nickname: selectedPin.text,
          linked_lift: selectedPin.id,
        },
      ],
      group_waypoints: Array.isArray(mapViewItem.group_waypoints)
        ? mapViewItem.group_waypoints
        : [],
      bank_waypoints: Array.isArray(mapViewItem.bank_waypoints) ? mapViewItem.bank_waypoints : [],
    };
    triggerPostMapViewPin({
      token,
      id: mapViewItem.id,
      body,
    });
    // turn of select mode
    dispatch(updateOpenSelectMode(!openSelectMode));
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  React.useEffect(() => {
    const pins = Array.isArray(mapViewItem?.waypoints) ? mapViewItem.waypoints : [];
    dispatch(
      updateMapViewPinArray(
        pins.map((pin: any) => ({
          ...pin,
          id: pin?.linked_lift || pin?.linkedLift || pin?.id,
          text: pin?.nickname || pin?.text || "",
          type: pin?.type || MapViewTypes.LIFT,
        })),
      ),
    );
    handleResize();
  }, [dispatch, mapViewItem]);

  React.useEffect(() => {
    window.addEventListener("resize", handleResize, false);
    handleResize();
  }, [containerRef.current?.clientWidth, containerRef.current?.clientHeight]);

  React.useEffect(() => {
    const svgUrl =
      mapViewItem?.map_img_url ||
      mapViewItem?.mapImgUrl ||
      mapViewItem?.s3_url ||
      mapViewItem?.offline_url ||
      "";
    setSvgSrc(resolvePublicAssetUrl(svgUrl));
  }, [mapViewItem]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <>
      {/** BODY */}
      <div
        style={{
          position: "relative",
        }}
      >
        {/** CUSTOM CURSOR */}
        {selectedPin && openSelectMode && (
          <div
            style={{
              position: "absolute",
              top: y ? y - getYMargin(selectedPin.direction) : 0,
              left: x ? x - getXMargin(selectedPin.direction) : 0,
              pointerEvents: "none",
            }}
          >
            <MapViewPin
              direction={getDirection(selectedPin.direction)}
              liftType={getTypeInLowerCase(selectedPin.type)}
              orientation={
                selectedPin.direction === "DOWN" || selectedPin.direction === "UP"
                  ? "vertical"
                  : "horizontal"
              }
              icon={getLiftIcon(selectedPin.type)}
              carName={selectedPin.text}
              isCursor
            />
          </div>
        )}
        {/** MAP */}
        <div
          ref={containerRef}
          style={{
            filter: openSelectMode && "brightness(0.8)",
            width: "100%",
            height: "fit-content",
          }}
          onClick={handleClickMap}
          role="button"
          tabIndex={0}
          aria-label="map"
          onKeyDown={emptyFunctionDeclaration}
        >
          {svgSrc ? (
            <img
              src={svgSrc}
              alt="Map"
              onLoad={handleResize}
              style={{ width: "100%", height: "auto", display: "block", objectFit: "contain" }}
            />
          ) : null}
        </div>
        {/** Pins */}
        {mapViewPinArray?.map((lift: any) => {
          if (!lift.x || !lift.y) return null;
          return (
            <div
              key={lift.id}
              style={{
                position: "absolute",
                top: lift.y * buttonProperties.height,
                left: lift.x * buttonProperties.width,
                pointerEvents: openSelectMode ? "none" : "auto",
              }}
            >
              <MapViewPin
                direction={getDirection(lift.direction)}
                liftType={getTypeInLowerCase(lift.type)}
                orientation={
                  lift.direction === "DOWN" || lift.direction === "UP" ? "vertical" : "horizontal"
                }
                icon={getLiftIcon(lift.type)}
                carName={lift.text}
                isCursor={false}
              />
            </div>
          );
        })}
      </div>
      <GlobalAlert2
        isError={isErrorPostMapView}
        isSuccess={isSuccessPostMapView}
        requestType="POST"
        error={errorPostMapView}
        name="Action"
      />
    </>
  );
}

export default MapViewMap;
